<template>
    <div class="price-tag-gallery" :class="{ 'price-tag-gallery--carousel': carousel }">
        <v-btn
            v-if="carousel"
            icon="mdi-chevron-left"
            variant="outlined"
            size="small"
            class="price-tag-gallery__nav"
            :disabled="!canScrollBack"
            :aria-label="$t('actions.previous')"
            @click="scrollBy(-1)"
        />
        <div ref="track" class="price-tag-gallery__track" @scroll="updateScrollState">
            <button
                v-for="item in items"
                :key="item.id"
                type="button"
                class="price-tag-gallery__item"
                :class="{ 'price-tag-gallery__item--selected': item.id === selectedId }"
                :disabled="disabled"
                :title="item.label"
                :data-id-e2e="`price-tag-${item.id}`"
                @click="emit('select', item.id)"
            >
                <div class="price-tag-gallery__thumb">
                    <div class="price-tag-gallery__scale">
                        <price-tag-renderer
                            :layout="item.priceTag.layout"
                            :values="getValues(item)"
                            :fields-by-key="keyFields(item.priceTag.fields)"
                            :context="getContext(item)"
                        />
                    </div>
                </div>
                <span class="price-tag-gallery__name">{{ item.label }}</span>
            </button>
        </div>
        <v-btn
            v-if="carousel"
            icon="mdi-chevron-right"
            variant="outlined"
            size="small"
            class="price-tag-gallery__nav"
            :disabled="!canScrollForward"
            :aria-label="$t('actions.next')"
            @click="scrollBy(1)"
        />
    </div>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from 'vue';
import { buildPriceTagContext } from '@/js/utils/price-tags/price-tag-context';
import {
    readValuesFromMechanic,
    applyValuesToMechanic,
} from '@/js/utils/price-tags/binding-engine';
import { keyFields } from '@/js/utils/price-tags/validate-values';

const props = defineProps({
    items: { type: Array, default: () => [] },
    selectedId: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    contextSource: { type: Object, required: true },
    carousel: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const track = ref(null);
const canScrollBack = ref(false);
const canScrollForward = ref(false);
const updateScrollState = () => {
    const element = track.value;
    if (!element) return;
    canScrollBack.value = element.scrollLeft > 0;
    canScrollForward.value = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
};
const scrollBy = direction => {
    track.value?.scrollBy({ left: direction * track.value.clientWidth * 0.8, behavior: 'smooth' });
};
const scrollSelectedIntoView = async () => {
    await nextTick();
    track.value
        ?.querySelector('.price-tag-gallery__item--selected')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    updateScrollState();
};
onMounted(() => {
    if (props.carousel) scrollSelectedIntoView();
});
watch(() => props.items.length, () => props.carousel && nextTick(updateScrollState));

const getValues = item =>
    readValuesFromMechanic({
        offerMechanic: item.offerMechanic,
        fields: item.priceTag.fields,
        displayValues: item.displayValues,
        transformContext: props.contextSource.transformContext,
    });
const getContext = item => {
    const { offerMechanic, displayValues } = applyValuesToMechanic({
        template: item.offerMechanic,
        fields: item.priceTag.fields,
        values: getValues(item),
        transformContext: props.contextSource.transformContext,
    });
    return buildPriceTagContext({
        ...props.contextSource,
        offerMechanic,
        displayValues,
        useStoredPrices: false,
    });
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

$thumb-scale: 0.3;

.price-tag-gallery {
    &__track {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
    }

    &--carousel {
        display: flex;
        gap: 0.8rem;
        align-items: center;

        .price-tag-gallery__track {
            flex: 1 1 auto;
            flex-wrap: nowrap;
            min-width: 0;
            overflow-x: auto;
            scroll-behavior: smooth;
            scroll-snap-type: x proximity;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }

        .price-tag-gallery__item {
            flex: 0 0 auto;
            scroll-snap-align: start;
        }
    }

    &__nav {
        flex: 0 0 auto;
    }

    &__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.4rem;
        padding: 0.4rem;
        border: 0.2rem solid transparent;
        border-radius: 0.4rem;
        background: none;
        cursor: pointer;

        &:hover:not(:disabled),
        &:focus-visible {
            border-color: $promo-grey-4;
        }

        &--selected {
            border-color: $promo-primary-colour;
        }

        &:disabled {
            cursor: default;
            opacity: 0.6;
        }
    }

    &__thumb {
        width: calc(52rem * #{$thumb-scale});
        height: calc(34rem * #{$thumb-scale});
        overflow: hidden;
    }

    &__scale {
        width: 52rem;
        transform: scale($thumb-scale);
        transform-origin: top left;
        pointer-events: none;
    }

    &__name {
        max-width: calc(52rem * #{$thumb-scale});
        font-size: 1.1rem;
        text-align: center;
    }
}
</style>
